import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from '@/api/business';
import { updateNonResearchReason } from '@/api/business/nonResearchReason';
import { NonResearchReason } from '@/types/NonResearchReason';
import Button from '@/components/Button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';

interface EditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  reason: NonResearchReason | null;
}

const formSchema = z.object({
  DS_MOTIVO_NAO_PESQUISA: z
    .string()
    .min(1, 'A descrição do motivo é obrigatória')
    .max(255, 'Máximo de 255 caracteres'),
  ATIVO: z.string(),
});

type FormSchema = z.infer<typeof formSchema>;

const EditDialog = ({ isOpen, onClose, reason }: EditDialogProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      DS_MOTIVO_NAO_PESQUISA: '',
      ATIVO: '1',
    },
  });

  useEffect(() => {
    if (reason && isOpen) {
      form.reset({
        DS_MOTIVO_NAO_PESQUISA: reason.DS_MOTIVO_NAO_PESQUISA || '',
        ATIVO: reason.ATIVO === 1 || reason.ATIVO === undefined ? '1' : '0',
      });
    }
  }, [reason, isOpen, form]);

  const { mutate, isPending } = useMutation({
    mutationFn: updateNonResearchReason,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['non-research-reason'] });
      toast({
        title: 'Sucesso',
        description: 'Motivo de não pesquisa atualizado com sucesso!',
      });
      onClose();
    },
    onError: (err) => {
      if (isAxiosError(err)) {
        toast({
          title: 'Erro ao atualizar',
          description:
            err.response?.data?.message ||
            'Não foi possível atualizar o motivo.',
          variant: 'destructive',
        });
      }
    },
  });

  const onSubmit = (values: FormSchema) => {
    if (!reason) return;
    mutate({
      SK_MOTIVO_NAO_PESQUISA: reason.SK_MOTIVO_NAO_PESQUISA,
      DS_MOTIVO_NAO_PESQUISA: values.DS_MOTIVO_NAO_PESQUISA,
      ATIVO: Number(values.ATIVO),
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Editar Motivo de Não Pesquisa</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="DS_MOTIVO_NAO_PESQUISA"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição do Motivo</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ex: Estabelecimento fechado"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="ATIVO"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Situação</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a situação" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="1">Ativo</SelectItem>
                      <SelectItem value="0">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button variant="outline" type="button" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Salvar...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditDialog;
